import React, { Suspense, lazy } from 'react';
import {
    BrowserRouter,
    Routes,
    Route,
    Navigate,
} from 'react-router-dom';
import { ClipLoader } from 'react-spinners';
import {
    FrontLayout,
    OwnerLayout
} from './layout';
import PrivateRoute from './PrivateRoute';
import { Toaster } from 'react-hot-toast';
import LocalStorage from './LocalStorage';
import { can } from '../constants/permissions';
import { SidebarData } from './layout/Owner/SidebarData';
/**
 * Eagerly-loaded pages
 * Landing is the public homepage ("/") and UserLogin is the login page ("/login") —
 * keep both eager so they render instantly with no Suspense flash. PageNotFound is
 * tiny. Everything else is code-split below so the public pages no longer ship
 * Chart.js, data tables, QR, sortable, moment, etc. in the initial bundle.
 */
import Landing from '../pages/landing/Landing';
import UserLogin from '../pages/login';
import StaffLogin from '../pages/login/StaffLogin';
import PageNotFound from './PageNotFound';

/**
 * Lazily-loaded pages (separate chunks, fetched on demand).
 * Named exports are unwrapped to the { default } shape React.lazy requires.
 */
const ForgotPassword = lazy(() => import('../pages/forgotPassword'));
const ResetPassword = lazy(() => import('../pages/forgotPassword/ResetPassword'));
const Legal = lazy(() => import('../pages/legal/Legal'));
const VerifyEmail = lazy(() => import('../pages/verifyEmail/VerifyEmail'));
const Register = lazy(() => import('../pages/register'));
const Pricing = lazy(() => import('../pages/pricing/Pricing'));
const Discover = lazy(() => import('../pages/discover/Discover'));
const VenuePage = lazy(() => import('../pages/discover/VenuePage'));
// Venue owner pages (the heavy authenticated app)
const OwnerDashboard = lazy(() => import('../pages/owner/dashboard'));
const MenuList = lazy(() => import('../pages/owner/menuManagement/MenuList'));
const MenuScratch = lazy(() => import('../pages/owner/menuManagement/MenuScratch'));
const ModifierGroups = lazy(() => import('../pages/owner/menuManagement/ModifierGroups'));
const OrderList = lazy(() => import('../pages/owner/order/OrderList'));
const PointOfSales = lazy(() => import('../pages/owner/pointOfSales/PointOfSales'));
const Settings = lazy(() => import('../pages/owner/settings/Settings'));
const Customers = lazy(() => import('../pages/owner/customers/Customers'));
const Regulars = lazy(() => import('../pages/owner/customers/Regulars'));
const Loyalty = lazy(() => import('../pages/owner/loyalty/Loyalty'));
const Reports = lazy(() => import('../pages/owner/reports/Reports'));
const Billing = lazy(() => import('../pages/owner/billing/Billing'));
const FloorScreen = lazy(() => import('../pages/owner/floor/FloorScreen'));
const ComingSoon = lazy(() => import('../pages/owner/ComingSoon'));
// Platform super-admin (god-view) — self-contained, own token, own layout
const AdminLogin = lazy(() => import('../pages/admin/AdminLogin'));
const AdminVenues = lazy(() => import('../pages/admin/AdminVenues'));
const AdminSignups = lazy(() => import('../pages/admin/AdminSignups'));
const AdminActivity = lazy(() => import('../pages/admin/AdminActivity'));
const AdminEmail = lazy(() => import('../pages/admin/AdminEmail'));
const AdminVenueDetail = lazy(() => import('../pages/admin/AdminVenueDetail'));
const AdminTestimonials = lazy(() => import('../pages/admin/AdminTestimonials'));

const RouteFallback = () => (
    <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '60vh',
    }}>
        <ClipLoader color="#FF7B2C" size={48} />
    </div>
);

/**
 * PermRoute — wraps a private owner page with a role permission check.
 * If the logged-in user's role lacks the perm, redirect to the first nav
 * item their role IS allowed to see (or /owner/dashboard as last resort).
 *
 * Owner (role falsy/'owner') always passes — existing behaviour unchanged.
 */
const PermRoute = ({ perm, element }) => {
    const authUser = LocalStorage.getAuthUser();
    const role = authUser?.role || null;
    if (can(role, perm)) return element;
    // find first permitted nav item as fallback landing
    const firstAllowed = SidebarData.find(item => can(role, item.perm));
    return <Navigate to={firstAllowed ? firstAllowed.path : '/owner/dashboard'} replace />;
};

const AppRoutes = (props) => {
    return (
        <BrowserRouter {...props}>
            {/* Flash banners: auto-dismiss after 7s with react-hot-toast's built-in fade (directive #2) */}
            <Toaster position='top-center' toastOptions={{ duration: 7000 }} />
            <Suspense fallback={<RouteFallback />}>
                <Routes>
                    {/*** Front Layout ***/}
                    <Route element={<FrontLayout />}>
                        <Route path='/register' element={<Register />} />
                        <Route path='/forgot-password' element={<ForgotPassword />} />
                        <Route path='/reset-password' element={<ResetPassword />} />
                        <Route path='/legal/:page' element={<Legal />} />
                        <Route path='/verify-email' element={<VerifyEmail />} />
                        <Route path='*' exact={true} element={<PageNotFound />} />
                    </Route>
                    {/*** Owner Layout (guarded; only /owner/* — must NOT claim bare "/" or it hijacks the homepage) ***/}
                    <Route path='/owner' element={<PrivateRoute />}>
                        <Route path="*" element={<OwnerLayout />}>
                            {/*** After Logged-In — each route guarded by role permission ***/}
                            <Route path='pos-dashboard' element={<PermRoute perm='pos' element={<PointOfSales />} />} />
                            <Route path='dashboard' element={<PermRoute perm='orders' element={<OwnerDashboard />} />} />
                            <Route path='menu-management' element={<PermRoute perm='menu' element={<MenuList />} />} />
                            <Route path='menu-scratch' element={<PermRoute perm='menu' element={<MenuScratch />} />} />
                            <Route path='modifier-groups' element={<PermRoute perm='modifiers' element={<ModifierGroups />} />} />
                            <Route path='orders' element={<PermRoute perm='orders' element={<OrderList />} />} />
                            <Route path='reports' element={<PermRoute perm='reports' element={<Reports />} />} />
                            <Route path='clients' element={<PermRoute perm='customers' element={<Customers />} />} />
                            <Route path='regulars' element={<PermRoute perm='customers' element={<Regulars />} />} />
                            <Route path='loyalty' element={<PermRoute perm='customers' element={<Loyalty />} />} />
                            <Route path='settings' element={<PermRoute perm='settings' element={<Settings />} />} />
                            <Route path='billing' element={<PermRoute perm='billing' element={<Billing />} />} />
                            <Route path='floor' element={<PermRoute perm='tables' element={<FloorScreen />} />} />
                            <Route path='reviews-feedback' element={<ComingSoon title='Reviews & Feedback' />} />
                            <Route path='coupons' element={<ComingSoon title='Coupons' />} />
                            <Route path='reward-points' element={<ComingSoon title='Reward Points' />} />
                            <Route path='translations' element={<ComingSoon title='Translations' />} />
                            <Route path='*' exact={true} element={<PageNotFound />} />
                        </Route>
                    </Route>
                    {/*** Public pricing — standalone, full-width (no narrow FrontLayout column) ***/}
                    <Route path='/' element={<Landing />} />
                    <Route path='/login' element={<UserLogin />} />
                    {/*** Staff sign-in — standalone full-page (own MUI card, not the narrow FrontLayout) ***/}
                    <Route path='/staff-login' element={<StaffLogin />} />
                    <Route path='/pricing' element={<Pricing />} />
                    <Route path='/discover' element={<Discover />} />
                    <Route path='/discover/:slug' element={<VenuePage />} />
                    {/*** Platform super-admin (god-view) — standalone, self-guarded ***/}
                    <Route path='/admin' element={<AdminLogin />} />
                    <Route path='/admin/venues' element={<AdminVenues />} />
                    <Route path='/admin/signups' element={<AdminSignups />} />
                    <Route path='/admin/activity' element={<AdminActivity />} />
                    <Route path='/admin/email' element={<AdminEmail />} />
                    <Route path='/admin/testimonials' element={<AdminTestimonials />} />
                    <Route path='/admin/venues/:id' element={<AdminVenueDetail />} />
                    <Route path='*' exact={true} element={<PageNotFound />} />
                </Routes>
            </Suspense>
        </BrowserRouter >
    )
}

export default AppRoutes;
